import { hookstate, useHookstate } from '@hookstate/core';
import { message } from 'antd';
import React, { useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { authActions } from '../../actions/auth';
import authSlice from '../../utils/redux/slice/authSlice';

const defaultProps = {
  state: hookstate({
    loading: true,
    error: null,
  }),
};

function SplashScreen({ state } = defaultProps) {
  const dispatch = useDispatch();
  const appState = useHookstate(state);
  const hasRun = useRef(false); // ✅ Prevents double execution
  // ✅ Memoized function prevents unnecessary recreation
  const verifyToken = useCallback(async () => {
    try {
      const data = await authActions.verifyToken();

      if (data === -1) {
        // Access token is dead — try the refresh token before giving up the
        // 30-day session; only a rejected refresh means actually logged out
        const refreshed = await authActions.tryRefreshSession();
        if (refreshed) {
          dispatch(authSlice.actions.setUser(refreshed.user));
          dispatch(authSlice.actions.setToken(refreshed.token));
          dispatch(authSlice.actions.setRefreshToken(refreshed.refreshToken));
          appState.loading.set(false);
          return;
        }
        authActions.performLogout({ callServer: false });
        appState.loading.set(false);
        // Let Navigation guard the requested route after logout. Redirecting
        // here discarded public deep links such as /connect and /register.
        return;
      }
      if (data === -2) {
        appState.loading.set(false);
        appState.nested('error').set('network');
        return;
      }

      dispatch(authSlice.actions.setUser(data.user));
      dispatch(authSlice.actions.setToken(data.token));
      // verify-token does NOT return a refreshToken — dispatching undefined
      // here wiped the stored refresh token on every page load
      if (data.refreshToken) {
        dispatch(authSlice.actions.setRefreshToken(data.refreshToken));
      }
      dispatch(authSlice.actions.setPermissions(data.permissions));

      appState.loading.set(false);
    } catch (error) {
      message.error(error.message);
      appState.nested('error').set(error.message);
      appState.loading.set(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]); // ✅ Only dependent on dispatch (no unnecessary dependencies)

  useEffect(() => {
    if (hasRun.current) return; // ✅ Stops multiple calls in StrictMode
    hasRun.current = true; // ✅ Marks execution

    verifyToken();
  }, [verifyToken]); // ✅ Runs only once

  if (appState.loading.get()) {
    return <div>Loading...</div>;
  }

  // hookstate returns undefined for a null-valued child — optional-chain it
  const errorVal = appState.error?.get?.();
  if (errorVal) {
    // Case-insensitive: axios throws "Network Error" (capital N)
    const isNetwork = String(errorVal).toLowerCase().includes('network')
      || String(errorVal).toLowerCase().includes('verify');
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0a0a0c', gap: 16 }}>
        <div style={{ fontSize: 48 }}>📡</div>
        <div style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>{isNetwork ? 'Connection problem' : 'Something went wrong'}</div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,.5)' }}>{isNetwork ? 'Check your internet connection and try again' : 'Please try again'}</div>
        <button onClick={() => window.location.reload()} style={{ marginTop: 8, padding: '10px 24px', background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.15)', borderRadius: 10, color: '#fff', fontSize: 14, cursor: 'pointer' }}>
          Try again
        </button>
      </div>
    );
  }

  return <div>SplashScreen</div>;
}

export default SplashScreen;
